import React, { Fragment, useCallback, useEffect, useRef, useMemo, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import ExitingPersistence from '@atlaskit/motion/exiting-persistence';
import FadeIn from '@atlaskit/motion/fade-in';

import { useContentState } from '@confluence/content-state/entry-points/useContentState.ts';
import { useElementSeenCallback } from '@confluence/analytics/entry-points/useElementSeenCallback.ts';
import { ContentAppearanceType } from '@confluence/content-appearance/entry-points/contentAppearanceTypes.ts';
import type { GlobalPageCard } from '@confluence/page-card/entry-points/cardTypes.ts';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd.ts';
import { useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';
import { expVal } from '@confluence/feature-experiments';
import type { SummaryAnalyticsPropertiesType } from '@confluence/quick-summary/entry-points/summarySharedTypes.ts';

import { EndOfPageRecGrid } from './EndOfPageRecGrid.tsx';
import { EndOfPageRecommendationCompactHeader } from './EndOfPageRecommendationCompactHeader.tsx';
import type { EndOfPageRecommendationQuery as EndOfPageRecommendationData } from './__types__/EndOfPageRecommendationQuery.ts';
import { END_OF_PAGE_RECOMMENDATIONS_METRIC } from './perf.config.ts';
import { EndOfPageRecContainer } from './EndOfPageRecContainer.tsx';

type EndOfPageRecommendationComponentProps = {
	entityId: string;
	entityType: string;
	pageCardData: GlobalPageCard[] | undefined;
	data: EndOfPageRecommendationData | undefined;
	pageWidthType: string;
	spaceKey: string;
	isLivePage?: boolean;
	// Overrides for the TWG arm (no cc-smarts `data`); derived from `data` when omitted.
	spaceIdOverride?: string;
	recommendationStrategyLabel?: string;
};

const GRID_CARD_LIMIT = 6;
const GRID_CARD_LIMIT_LIVE_PAGES = 4;

export const EndOfPageRecommendationComponent = ({
	entityId,
	entityType,
	pageCardData,
	data,
	pageWidthType,
	isLivePage,
	spaceKey,
	spaceIdOverride,
	recommendationStrategyLabel,
}: EndOfPageRecommendationComponentProps): React.JSX.Element | null => {
	const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isInfoOpen, setInfoOpen] = useState(false);
	const compRef = useRef<HTMLDivElement | null>(null);
	const infoButtonRef = useRef<HTMLElement | null>(null);

	// eslint-disable-next-line confluence-sweet-state/sweet-state-prefer-actions-hook
	const [, actions] = useContentState();

	const gridCardLimit = isLivePage ? GRID_CARD_LIMIT_LIVE_PAGES : GRID_CARD_LIMIT;

	const pageRecIds = useMemo(
		() => pageCardData?.slice(0, gridCardLimit).map((content) => content.id),
		[pageCardData, gridCardLimit],
	);

	const contentRecsStrategies = useMemo(() => {
		// TWG arm has no per-rec `strategy`; tag only the slots with actual cards with
		// the label and leave the rest as 'NULL', mirroring the cc-smarts padding below
		// so both arms emit structurally comparable analytics arrays.
		if (recommendationStrategyLabel) {
			const strategiesArray = Array(gridCardLimit).fill('NULL');
			const recCount = Math.min(pageCardData?.length ?? 0, gridCardLimit);
			for (let index = 0; index < recCount; index++) {
				strategiesArray[index] = recommendationStrategyLabel;
			}
			return strategiesArray;
		}

		const recommendations =
			data?.getRecommendedPages?.recommendedPages?.slice(0, gridCardLimit) || [];
		const strategiesArray = Array(gridCardLimit).fill('NULL');

		recommendations.forEach((rec, index) => {
			// Convert strategy arrays to simplified format for analytics
			// BEHAVIORAL -> B, CONTENT_TO_CONTENT -> C, [CONTENT_TO_CONTENT, BEHAVIORAL] -> BC, etc...
			strategiesArray[index] = rec.strategy
				.map((s) => s.charAt(0))
				.sort()
				.join('');
		});

		return strategiesArray;
	}, [data, gridCardLimit, recommendationStrategyLabel, pageCardData]);

	const spaceId = spaceIdOverride ?? data?.space?.id;

	const toggleInfo = useCallback(() => {
		if (!isInfoOpen) {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: entityType,
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'endOfPageRecommendationsInfoButton',
					attributes: {
						contentId: entityId,
					},
				},
			}).fire();
		}

		setInfoOpen(!isInfoOpen);
	}, [createAnalyticsEvent, isInfoOpen, entityId, entityType]);

	const onObserved = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				source: entityType,
				action: 'observed',
				actionSubject: 'endOfPageRecommendations',
				attributes: {
					strategies: contentRecsStrategies,
				},
				objectId: entityId,
				containerId: spaceId,
			},
		}).fire();
	}, [createAnalyticsEvent, entityId, contentRecsStrategies, spaceId, entityType]);

	useElementSeenCallback(compRef, onObserved);

	useEffect(() => {
		if (!!pageRecIds?.length && pageRecIds?.length > 1) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					source: entityType,
					action: 'rendered',
					actionSubject: 'endOfPageRecommendations',
					attributes: {
						strategies: contentRecsStrategies,
					},
					objectId: entityId,
					containerId: spaceId,
				},
			}).fire();
		}
	}, [createAnalyticsEvent, entityId, contentRecsStrategies, pageRecIds, spaceId, entityType]);

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const isSSRed = process.env.REACT_SSR || window.__SSR_RENDERED__;

	const handleInfoButtonRef = (triggerProps) => {
		return (node) => {
			infoButtonRef.current = node;
			triggerProps.ref(node);
		};
	};

	const isDefaultWidth = pageWidthType
		? JSON.parse(pageWidthType) === ContentAppearanceType.DEFAULT
		: false;

	const analyticsProperties: SummaryAnalyticsPropertiesType = useMemo(
		() => ({
			source: 'endOfPageRecommendation',
			additionalAnalyticsAttributes: {
				contentType: entityType,
				strategies: contentRecsStrategies,
				spaceKey,
				spaceId,
				recommendationCount: pageCardData?.length || 0,
			},
		}),
		[entityType, contentRecsStrategies, spaceKey, spaceId, pageCardData?.length],
	);

	return !!pageCardData?.length ? (
		<Fragment>
			{!isSSRed && (actions?.addContentToBeLoaded('endOfPageRecommendation', 'loading'), null)}
			<EndOfPageRecContainer
				isDefaultWidth={isDefaultWidth}
				ssrPlaceholderIdProp={ssrPlaceholderIdProp}
				isCompactRelatedContentSpecSpacing={expVal(
					'confluence_related_pages_remove_feedback_icons',
					'isEnabled',
					false,
				)}
			>
				<EndOfPageRecommendationCompactHeader
					toggleInfo={toggleInfo}
					isInfoOpen={isInfoOpen}
					handleInfoButtonRef={handleInfoButtonRef}
					analyticsProperties={analyticsProperties}
					contentId={entityId}
				/>

				<ExitingPersistence appear={false}>
					<FadeIn entranceDirection="top" exitDirection="top" duration="medium">
						{(props) => (
							<div {...props}>
								<EndOfPageRecGrid
									isCompact
									pageCardData={pageCardData.slice(0, gridCardLimit)}
									pageRecStrategy={contentRecsStrategies}
									entityId={entityId}
								/>
								<div ref={compRef} />
							</div>
						)}
					</FadeIn>
				</ExitingPersistence>
			</EndOfPageRecContainer>
			<PageSegmentLoadEnd key={entityId} metric={END_OF_PAGE_RECOMMENDATIONS_METRIC} />
			{!isSSRed && (actions?.removeContentFromLoading('endOfPageRecommendation'), null)}
		</Fragment>
	) : null;
};
